import { useEffect, useRef, type ReactNode } from 'react';
import { ArrowLeft, PanelRight, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { initials } from '@/lib/conversationFormat';

type Props = {
  name: string;
  /** Second line: number, agent line, window. */
  meta: ReactNode;
  avatarName: string | null;
  avatarPhone: string;
  /** Small marks next to the name (LIVE, Test …). */
  badges?: ReactNode;
  /** Pulsing ring around the avatar (running call). */
  live?: boolean;
  /** Phone call: an unnamed caller gets a phone glyph instead of two digits. */
  phoneCall?: boolean;
  /** Phone: back to the list. */
  onBack?: () => void;
  onToggleContext?: () => void;
  contextOpen?: boolean;
  headingId: string;
};

/** Head of the chat column: who, over which line, and the context toggle. */
export function ChatHeader({
  name,
  meta,
  avatarName,
  avatarPhone,
  badges,
  live = false,
  phoneCall = false,
  onBack,
  onToggleContext,
  contextOpen = false,
  headingId,
}: Props) {
  const backRef = useRef<HTMLButtonElement>(null);

  // The loading placeholder had its own back arrow; when the real header
  // replaces it the focus would drop to the page (or the dialog frame).
  // Give it back to the arrow so keyboard and screen-reader users keep their place.
  useEffect(() => {
    const active = document.activeElement;
    if (onBack && (!active || active === document.body || active.getAttribute('role') === 'dialog')) {
      backRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when the header appears
  }, []);

  return (
    <header className="flex flex-shrink-0 items-center gap-3 border-b border-border bg-card px-3 py-3 sm:px-5">
      {onBack && (
        <button
          ref={backRef}
          type="button"
          onClick={onBack}
          className="-ml-1 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          aria-label="Zurück zur Liste"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
      <span
        className={cn(
          'relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold text-foreground/80',
          live && 'text-brand',
        )}
        aria-hidden="true"
      >
        {phoneCall && !avatarName?.trim() ? <Phone className="h-4 w-4" /> : initials(avatarName, avatarPhone)}
        {live && (
          <span className="absolute -inset-1 rounded-full border-2 border-brand motion-safe:animate-pulse" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <h2 id={headingId} className="truncate text-[17px] font-semibold leading-tight tracking-tight text-foreground">
            {name}
          </h2>
          {badges}
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{meta}</p>
      </div>
      {onToggleContext && (
        <button
          type="button"
          onClick={onToggleContext}
          aria-pressed={contextOpen}
          aria-label="Kontext anzeigen"
          title="Kontext: Kunde, Übergabe, Sitzungen"
          className={cn(
            'inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
            contextOpen
              ? 'border-brand/30 bg-brand-soft text-brand'
              : 'border-transparent text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
        >
          <PanelRight className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </header>
  );
}

/** "LIVE" mark for running calls and fresh chats. */
export function LiveMark({ label = 'Live' }: { label?: string }) {
  return (
    <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-brand-foreground">
      <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full rounded-full bg-brand-foreground opacity-70 motion-safe:animate-ping" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand-foreground" />
      </span>
      {label}
    </span>
  );
}

export function TestMark() {
  return (
    <span
      className="flex-shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
      title="Von der Testumgebung erzeugt – kein echter Anruf/Chat"
    >
      Test
    </span>
  );
}

export default ChatHeader;
